import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  inject,
  ChangeDetectorRef,
  Self,
  Optional,
  OnChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IconComponent } from '../../icons';

/**
 * Component to wrap the native textarea element with Tailwind styling and
 * a11y handling for errors and helper text.  By default this textarea appears
 * like a one-line input but will expand as the user types.
 *
 * Notable Issues:
 * - does not handle shift-enter expanding the height of the box
 * - no support for a character counter yet
 *
 * @see `InputComponent`
 *
 * @alpha
 * Split textarea out from input but it is very similar at the moment.
 * Still needs some discussion with UX about the differences to support.
 * Should fit basic usage similar to InputComponent, though.
 *
 * @usageNotes
 * Make sure to set the `inputId` and not `id` or else you will have strange behavior.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'da-textarea',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IconComponent],
  styles: `
    /* tailwind version isn't being applied */
    .resize-none {
      resize: none;
    }
  `,
  template: `
    <div
      class="tw-bottom-1 tw-col-span-full sm:tw-col-span-7 md:tw-col-span-5 md:tw-col-start-2 lg:tw-col-span-8 lg:tw-col-start-2 xl:tw-col-span-6 xl:tw-col-start-3"
    >
      <div
        class="tw-group tw-relative tw-z-0 tw-w-full"
        [ngClass]="{ 'has-error': !!isInvalid }"
      >
        <textarea
          #textInput
          [id]="inputId"
          (input)="onInputChange($event)"
          (focus)="onFocus($event)"
          (blur)="onTouched($event)"
          (change)="handleEvent('change', $event)"
          [attr.rows]="calculateNumLines().numRows"
          [required]="required"
          [attr.aria-invalid]="!!isInvalid"
          [attr.aria-describedby]="
            inputId +
            '-readonly ' +
            inputId +
            '-error ' +
            inputId +
            '-helper' +
            ' ' +
            describedByIds
          "
          class="resize-none group-[.has-error]:[&amp;:not(:focus)]:tw-pr-6 focus:[&amp;:not(:placeholder-shown)]:tw-pr-6 group-hover:[&amp;:not(:placeholder-shown)]:tw-pr-6 tw-peer tw-mt-4 tw-block tw-w-full tw-cursor-text tw-appearance-none tw-border-0 tw-border-b-2 tw-border-neutral-200 tw-bg-transparent tw-px-0 tw-py-1 tw-text-neutral-800 tw-placeholder-neutral-600 placeholder:tw-opacity-0 read-only:tw-cursor-not-allowed read-only:tw-border-dashed read-only:tw-bg-neutral-100 focus:tw-border-blue-800 focus:tw-outline-none focus:tw-ring-0 focus:placeholder:tw-opacity-100 group-[.has-error]:tw-border-red-200 group-[.has-error]:tw-text-red-800 group-[.has-error]:tw-placeholder-red-800 focus:group-[.has-error]:tw-border-red-800 dark:tw-border-neutral-700 dark:tw-text-neutral-100 dark:tw-placeholder-neutral-400 dark:read-only:tw-bg-neutral-800 dark:focus:tw-border-blue-600 dark:group-[.has-error]:tw-border-red-700 dark:group-[.has-error]:tw-text-red-100 dark:group-[.has-error]:tw-placeholder-red-400 dark:focus:group-[.has-error]:tw-border-red-700"
          [ngClass]="{
            'tw-text-base': size === 'normal',
            'tw-text-3xl tw-font-bold placeholder:tw-font-bold':
              size === 'large',
          }"
          [attr.placeholder]="labelText"
          [attr.maxLength]="maxLength"
          [attr.readonly]="readonly ? '' : null"
          [value]="value"
        ></textarea>
        <button
          type="button"
          *ngIf="!readonly"
          (click)="resetField($event)"
          [class.tw-hidden]="!showResetButton"
          class="tw-dark:text-neutral-300 tw-absolute tw-right-0 tw-z-10 tw-hidden tw-items-center focus:tw-flex peer-focus:peer-[&:not(:placeholder-shown)]:peer-[&:not(:read-only)]:tw-flex group-hover:peer-enabled:peer-[&:not(:placeholder-shown)]:peer-[&:not(:read-only)]:tw-flex"
          [ngClass]="{
            'tw-top-3': size === 'normal',
            'tw-top-6': size === 'large',
          }"
          [attr.aria-labelledby]="inputId + '-clear ' + inputId + '-label'"
        >
          <da-icon
            name="close"
            type="solid"
            icon="x-mark"
            [solidSize]="16"
            [className]="'tw-size-4'"
          />
          <span class="tw-sr-only" [id]="inputId + '-clear'">
            {{ resetButtonLabel }}
          </span>
        </button>
        <da-icon
          *ngIf="readonly"
          name="lock"
          type="solid"
          icon="lock-closed"
          [solidSize]="16"
          class="tw-pointer-events-none tw-absolute tw-right-0 tw-flex tw-items-center tw-text-neutral-600"
          [ngClass]="{
            'tw-top-2': size === 'normal',
            'tw-top-3': size === 'large',
          }"
          [className]="'tw-size-4'"
        />
        <label
          [for]="inputId"
          [id]="inputId + '-label'"
          [ngClass]="{
            'tw-text-base peer-placeholder-shown:tw-text-base peer-placeholder-shown:tw-font-normal':
              size === 'normal',
            'tw-text-3xl tw-font-bold peer-placeholder-shown:tw-text-3xl peer-placeholder-shown:tw-font-bold':
              size === 'large',
          }"
          class="tw-absolute tw-top-1 tw-origin-[0] -tw-translate-y-5 tw-transform tw-cursor-text tw-text-xs tw-text-neutral-600 tw-duration-300 group-hover:tw-text-neutral-800 group-[.has-error]:tw-text-red-800 group-hover:group-[.has-error]:tw-text-red-800 peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-text-base peer-read-only:tw-cursor-not-allowed peer-focus:tw-left-0 peer-focus:-tw-translate-y-5 peer-focus:tw-text-xs peer-focus:tw-font-bold peer-focus:tw-text-blue-800 group-[.has-error]:peer-focus:tw-text-red-800 dark:tw-text-neutral-300 dark:group-hover:tw-text-neutral-200 dark:group-[.has-error]:tw-text-red-400 dark:group-hover:group-[.has-error]:tw-text-red-300 dark:peer-focus:tw-text-blue-600 dark:group-[.has-error]:peer-focus:tw-text-red-400"
        >
          {{ labelText }}
          <span *ngIf="required" aria-hidden="true" class="tw-text-red-800">
            *
          </span>
        </label>
        <span
          *ngIf="readonly"
          class="tw-sr-only"
          [id]="inputId + '-readonly'"
          >{{ readonlyDescription }}</span
        >
        <div
          *ngIf="helperText"
          [id]="inputId + '-helper'"
          class="tw-mt-1 tw-text-xs tw-text-neutral-600"
        >
          {{ helperText }}
        </div>
        <div
          [id]="inputId + '-error'"
          class="tw-mt-1 tw-text-xs tw-text-red-800"
          role="alert"
        >
          <div *ngFor="let error of errors">
            <span *ngIf="isInvalid && error.name">{{ error.message }}</span>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class TextareaComponent implements ControlValueAccessor, OnChanges {
  /** Characters per line before the textarea adds a row (default: 100) */
  @Input() public lineLength?: number = 100;
  /** String with space delimiters between ids to associate with the input using aria-describedby */
  @Input() public describedByIds?: string;
  /** Label for the input (required) */
  @Input() public labelText!: string;
  /** Additional guidance text, displays below the input line */
  @Input() public helperText?: string;
  /** ValidationErrors passed in (e.g. from a FormGroup) to manage error messages.  Without input it defaults to using whatever errors are on the default ngcontrol. */
  @Input() public errorOverrides?: ValidationErrors;
  /** Size mode ('normal' or 'large', default: 'normal') */
  @Input() public size: 'normal' | 'large' = 'normal';
  /** Number of rows for textarea (default: 1) */
  @Input() public rows?: number = 1;
  /** Whether the field is required (default: false) */
  @Input() public required: boolean = false;
  /** Maximum character length (default: 200) */
  @Input() public maxLength?: number = 200;
  /** Unique identifier for the input (required) */
  @Input() public inputId!: string;
  /** Mark the input as readonly / disabled */
  @Input() public readonly: boolean = false;
  /** Regex pattern to validate against */
  @Input() public pattern?: string | RegExp = '';
  /** Reference to the input element itself for manipulation */
  @ViewChild('textInput') public textInput!: ElementRef<HTMLTextAreaElement>;
  /** Emits { eventType: string, event: Event } on input changes */
  @Output() public inputEvent: EventEmitter<{
    eventType: string;
    event: Event;
  }> = new EventEmitter();

  /** Current value of the input, only used when not wrapped in a form. (default: '') */
  public value: string = '';
  /** Internal counter for number of rows displayed */
  public numRows: number;
  /** Internal state tracking of the reset button */
  public showResetButton: boolean = false;
  /** Translated label for the reset button (Core_Clear) */
  public resetButtonLabel = this.translate.instant('Core_Clear');
  /** Translated label for the readonly icon (Core_Readonly) */
  public readonlyDescription = this.translate.instant('Core_Readonly');

  private errorMessages = {
    pattern: this.translate.instant('Core_InvalidValue'),
    required: this.translate.instant('Core_FieldRequired'),
  };

  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  /**
   * Transforms ValidationErrors into a format that can be used with ngFor
   * to automatically handle error display.  Defautls to errors on the internal
   * ngControl, but will use the `errorOverrides` input instead if it exists.
   */
  public get errors(): { [key: string]: string }[] {
    // if we have no errors just return empty array
    if (!this.ngControl?.errors && !this.errorOverrides) return [];

    // otherwise we create an Angular-iterable array of error messages
    // out of the errors returned, defaulting to a default error message
    // if one matches a default error we provide a message for (e.g. required
    // or pattern), otherwise the field will display no error
    const errors = [];
    // if we pass in errors to the field (e.g. for a grouped field), prefer
    // that over the internal errors on the control.
    const validationErrors = this.errorOverrides
      ? this.errorOverrides
      : this.ngControl?.errors;
    // loop all the errors and push to an array that can be displayed on the page
    for (const [key, value] of Object.entries(validationErrors)) {
      const error = {
        name: key,
        message: value.message || this.errorMessages[key],
      };

      // create the error message object
      errors.push(error);
    }
    return errors;
  }

  /**
   * Checks if the field should be considered invalid.  Overridden by external
   * errors passed in, otherwise defaults to internal dirty/invalid check on the
   * form control associated with the input.
   */
  public get isInvalid(): boolean {
    return (
      (this.ngControl?.dirty && this.ngControl?.invalid) ||
      !!this.errorOverrides
    );
  }

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private translate: TranslateService
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  /**
   * Calculates the number of rows needed and determines if textarea should be used.
   * Blatantly copied from the react version.
   */
  public calculateNumLines(): { numRows: number } {
    const countLines = (text: string | undefined) =>
      (text || '')
        .split(/\r\n|\r|\n/g)
        .reduce(
          (acc, line) => acc + Math.ceil(line.length / this.lineLength),
          0
        );

    const textLength = this.value?.length;
    const numRows =
      this.rows && this.rows > 1
        ? this.rows
        : Math.max(
            countLines(this.value),
            Math.ceil(textLength / this.lineLength)
          ) || 1;

    return { numRows };
  }

  public ngOnChanges(): void {
    this.updateNumLines();
    this.cdr.detectChanges();
  }

  /**
   * Resets the input field, clearing its value and resetting related properties.
   */
  public resetField(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    // prefer using form controls if they exist
    if (this.ngControl) {
      this.ngControl.reset();
    } else {
      this.value = '';
    }

    this.showResetButton = false;
    const el = this.textInput.nativeElement;
    if (el instanceof HTMLTextAreaElement) {
      el.rows = 1;
    }
    el.focus();
  }

  public updateNumLines(): void {
    const { numRows } = this.calculateNumLines();
    this.numRows = numRows;
  }

  public onInputChange(event: Event): void {
    const inputElement = event.target as HTMLTextAreaElement;
    this.value = inputElement.value;
    this.showResetButton = this.value.length > 0;
    this.onChange(this.value);
    // this.updateNumLines();
  }

  public onFocus(event: Event): void {
    this.showResetButton = this.value?.length > 0;
    this.inputEvent.emit({ eventType: 'focus', event });
  }

  /**
   * Handle all the events.
   * @param eventType click, focus blur etc.
   * @param event the actual event
   */
  public handleEvent(eventType: string, event: Event) {
    this.inputEvent.emit({ eventType, event });
  }

  public writeValue(value: string): void {
    this.value = value;
    // this.updateNumLines();

    // needed to ensure things like form.reset will affect the field
    this.cdr.detectChanges();
  }

  public onChange: (value: string) => void = () => null;

  /**
   * Hide the clear/reset button.
   */
  public onTouched: (event: Event) => void = (event: Event) => {
    // Delay hiding the button to allow for click events
    // When relying on the tailwind peer selector the click event does not fire as it hides the button.
    this.inputEvent.emit({ eventType: 'focus', event });
    setTimeout(() => {
      this.showResetButton = false;
    }, 200);
  };

  /**
   * Required as ControlValueAccessor
   * @param fn
   */
  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  /**
   * Required as ControlValueAccessor
   * @param fn
   */
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Required as ControlValueAccessor
   * @param fn
   */
  public setDisabledState(isDisabled: boolean): void {
    this.readonly = isDisabled;
  }
}
